import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChatCapsulePreview,
  ChatMessage,
  Conversation,
  chatApi,
} from '@/services/chatApi';
import { usePolling } from './usePolling';

const CONVERSATIONS_POLL_MS = 10000;
const MESSAGES_POLL_MS = 3000;
const MESSAGES_PAGE_SIZE = 50;
const SUGGESTIONS_DEBOUNCE_MS = 250;

const createClientId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Список диалогов с фоновым обновлением. */
export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await chatApi.getConversations();
      setConversations(response.data?.conversations || []);
      setError(null);
    } catch {
      setError('chat.errors.conversationsLoad');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  usePolling(refresh, { intervalMs: CONVERSATIONS_POLL_MS });

  /** Локально обнуляет счётчик, чтобы бейдж не «мигал» до следующего опроса. */
  const clearUnread = useCallback((conversationId: string) => {
    setConversations(previous =>
      previous.map(conversation =>
        conversation.id === conversationId
          ? { ...conversation, unreadCount: 0 }
          : conversation
      )
    );
  }, []);

  const upsert = useCallback((conversation: Conversation) => {
    setConversations(previous => {
      const rest = previous.filter(item => item.id !== conversation.id);
      return [conversation, ...rest];
    });
  }, []);

  return {
    conversations,
    isLoading,
    error,
    refresh,
    clearUnread,
    upsert,
  };
}

interface UseConversationMessagesResult {
  messages: ChatMessage[];
  isLoading: boolean;
  isLoadingOlder: boolean;
  hasMore: boolean;
  error: string | null;
  loadOlder: () => Promise<void>;
  sendMessage: (payload: {
    text: string;
    capsules: ChatCapsulePreview[];
  }) => Promise<void>;
  retryMessage: (clientId: string) => Promise<void>;
}

/**
 * Переписка одного диалога.
 *
 * История подгружается страницами вверх, новые сообщения — инкрементально
 * по курсору последнего известного id. Отправка оптимистичная: сообщение
 * появляется сразу и заменяется подтверждённым ответом сервера.
 */
export function useConversationMessages(
  conversationId: string | undefined,
  options?: { onRead?: (conversationId: string) => void }
): UseConversationMessagesResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(conversationId));
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastSyncedIdRef = useRef<string | null>(null);
  const lastReadIdRef = useRef<string | null>(null);
  const onReadRef = useRef(options?.onRead);

  useEffect(() => {
    onReadRef.current = options?.onRead;
  }, [options?.onRead]);

  const markRead = useCallback(
    async (messageId: string) => {
      if (!conversationId || lastReadIdRef.current === messageId) return;
      lastReadIdRef.current = messageId;
      try {
        await chatApi.markRead(conversationId, messageId);
        onReadRef.current?.(conversationId);
      } catch {
        lastReadIdRef.current = null;
      }
    },
    [conversationId]
  );

  useEffect(() => {
    lastSyncedIdRef.current = null;
    lastReadIdRef.current = null;
    setMessages([]);
    setHasMore(false);
    setError(null);

    if (!conversationId) {
      setIsLoading(false);
      return;
    }

    let isActive = true;
    setIsLoading(true);

    chatApi
      .getMessages(conversationId, { limit: MESSAGES_PAGE_SIZE })
      .then(response => {
        if (!isActive) return;
        const loaded = response.data?.messages || [];
        setMessages(loaded);
        setHasMore(Boolean(response.data?.hasMore));
        const newest = loaded[loaded.length - 1];
        if (newest) {
          lastSyncedIdRef.current = newest.id;
          void markRead(newest.id);
        }
      })
      .catch(() => {
        if (isActive) setError('chat.errors.messagesLoad');
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [conversationId, markRead]);

  const fetchNewMessages = useCallback(async () => {
    if (!conversationId || !lastSyncedIdRef.current) return;

    try {
      const response = await chatApi.getMessages(conversationId, {
        after: lastSyncedIdRef.current,
        limit: MESSAGES_PAGE_SIZE,
      });
      const incoming = response.data?.messages || [];
      if (incoming.length === 0) return;

      lastSyncedIdRef.current = incoming[incoming.length - 1].id;
      setMessages(previous => mergeMessages(previous, incoming));
      void markRead(incoming[incoming.length - 1].id);
    } catch {
      // Сетевой сбой опроса не должен ломать открытый чат — ждём следующий тик.
    }
  }, [conversationId, markRead]);

  usePolling(fetchNewMessages, {
    intervalMs: MESSAGES_POLL_MS,
    enabled: Boolean(conversationId),
  });

  const loadOlder = useCallback(async () => {
    const oldest = messages.find(message => !message.isPending);
    if (!conversationId || !oldest || !hasMore || isLoadingOlder) return;

    setIsLoadingOlder(true);
    try {
      const response = await chatApi.getMessages(conversationId, {
        before: oldest.id,
        limit: MESSAGES_PAGE_SIZE,
      });
      const older = response.data?.messages || [];
      setHasMore(Boolean(response.data?.hasMore));
      if (older.length > 0) {
        setMessages(previous => mergeMessages(older, previous));
      }
    } catch {
      setError('chat.errors.messagesLoad');
    } finally {
      setIsLoadingOlder(false);
    }
  }, [conversationId, hasMore, isLoadingOlder, messages]);

  const deliver = useCallback(
    async (draft: ChatMessage) => {
      if (!conversationId) return;

      try {
        const response = await chatApi.sendMessage(conversationId, {
          text: draft.text,
          capsuleIds: draft.capsules.map(capsule => capsule.id),
          clientId: draft.clientId || undefined,
        });
        const saved = response.data?.message;
        if (!saved) return;

        setMessages(previous =>
          mergeMessages(
            previous.filter(message => message.clientId !== draft.clientId),
            [saved]
          )
        );
        lastSyncedIdRef.current = maxId(lastSyncedIdRef.current, saved.id);
        void markRead(saved.id);
      } catch {
        setMessages(previous =>
          previous.map(message =>
            message.clientId === draft.clientId
              ? { ...message, isPending: false, isFailed: true }
              : message
          )
        );
      }
    },
    [conversationId, markRead]
  );

  const sendMessage = useCallback(
    async ({
      text,
      capsules,
    }: {
      text: string;
      capsules: ChatCapsulePreview[];
    }) => {
      if (!conversationId) return;

      const draft: ChatMessage = {
        id: `pending-${createClientId()}`,
        conversationId,
        kind: 'text',
        text,
        sender: null,
        createdAt: new Date().toISOString(),
        isDeleted: false,
        clientId: createClientId(),
        capsules,
        isPending: true,
      };

      setMessages(previous => [...previous, draft]);
      await deliver(draft);
    },
    [conversationId, deliver]
  );

  const retryMessage = useCallback(
    async (clientId: string) => {
      const failed = messages.find(message => message.clientId === clientId);
      if (!failed) return;

      setMessages(previous =>
        previous.map(message =>
          message.clientId === clientId
            ? { ...message, isPending: true, isFailed: false }
            : message
        )
      );
      await deliver({ ...failed, isPending: true, isFailed: false });
    },
    [deliver, messages]
  );

  return {
    messages,
    isLoading,
    isLoadingOlder,
    hasMore,
    error,
    loadOlder,
    sendMessage,
    retryMessage,
  };
}

/** Подсказки капсул для «/»-меню: запрос уходит только когда меню открыто. */
export function useCapsuleSuggestions(query: string | null) {
  const [suggestions, setSuggestions] = useState<ChatCapsulePreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (query === null) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    let isActive = true;
    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      chatApi
        .getCapsuleSuggestions(query)
        .then(response => {
          if (isActive) setSuggestions(response.data?.capsules || []);
        })
        .catch(() => {
          if (isActive) setSuggestions([]);
        })
        .finally(() => {
          if (isActive) setIsLoading(false);
        });
    }, SUGGESTIONS_DEBOUNCE_MS);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [query]);

  return { suggestions, isLoading };
}

/**
 * Общее число непрочитанных сообщений — для индикатора в шапке.
 * На публичных страницах опрос выключается параметром `enabled`.
 */
export function useUnreadChatCount(enabled = true) {
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await chatApi.getUnreadCount();
      setUnreadCount(response.data?.unreadCount || 0);
    } catch {
      // Индикатор не критичен: при ошибке просто оставляем прежнее значение.
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  usePolling(refresh, { intervalMs: CONVERSATIONS_POLL_MS, enabled });

  return unreadCount;
}

/** Сообщения из разных источников склеиваются без дублей и в порядке id. */
function mergeMessages(
  current: ChatMessage[],
  incoming: ChatMessage[]
): ChatMessage[] {
  const byKey = new Map<string, ChatMessage>();

  [...current, ...incoming].forEach(message => {
    byKey.set(message.clientId || message.id, message);
  });

  return Array.from(byKey.values()).sort(compareMessages);
}

function compareMessages(a: ChatMessage, b: ChatMessage): number {
  // Неотправленные сообщения всегда остаются в конце ленты.
  const aPending = Boolean(a.isPending || a.isFailed);
  const bPending = Boolean(b.isPending || b.isFailed);

  if (aPending !== bPending) return aPending ? 1 : -1;
  if (aPending && bPending) return a.createdAt.localeCompare(b.createdAt);
  return Number(a.id) - Number(b.id);
}

function maxId(a: string | null, b: string): string {
  if (!a) return b;
  return Number(b) > Number(a) ? b : a;
}

export const chatHelpers = { createClientId };

export function useConversationTitle(
  conversation: Conversation | undefined,
  fallback: string
): string {
  return useMemo(() => {
    if (!conversation) return fallback;
    if (conversation.kind === 'group') return conversation.title || fallback;
    return conversation.peer?.name || conversation.peer?.username || fallback;
  }, [conversation, fallback]);
}
